import { FileMetadataRequest, FileType } from '../../types/file';
import { NewProjectRequest } from '../../types/project';
import { UnitSystem } from '../../types/util';
import {
  CAD_MODEL_FILE_NAME,
  DrawingDocument,
  computeOrigin,
  polygonsToMeshes,
  toDrawingDocument,
  toModelPolygons,
} from './drawingDocument';
import { generateProjectGlb } from './generateProjectGlb';
import { CadPlan } from './useCadState';

/** A plan image as base64 (no data: prefix) with its natural size. */
async function readImage(url: string): Promise<{ base64: string; width: number; height: number }> {
  const blob = await (await fetch(url)).blob();
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  const { width, height } = await new Promise<{ width: number; height: number }>((resolve, reject) => {
    const probe = new Image();
    probe.onload = () => resolve({ width: probe.naturalWidth, height: probe.naturalHeight });
    probe.onerror = reject;
    probe.src = dataUrl;
  });
  const comma = dataUrl.indexOf(',');
  return { base64: comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl, width, height };
}

/** The model built from a plan, around the given origin (metres subtracted from the scaled points). */
export async function buildPlanModel(
  plan: Pick<CadPlan, 'polygons' | 'metersPerUnit'>,
  origin: [number, number],
): Promise<{ model: File; meshNames: string[] }> {
  const { blob, meshNames } = await generateProjectGlb(toModelPolygons(plan.polygons, plan.metersPerUnit, origin), {
    recenter: false,
  });
  return { model: new File([blob], CAD_MODEL_FILE_NAME, { type: 'model/gltf-binary' }), meshNames };
}

/** The plan's image for a project: base64, metres per image pixel and its centre in metres. */
export async function planImage(
  imageUrl: string | null,
  imageScale: number | null,
  metersPerUnit: number,
): Promise<{ base64: string; modelScale: number; center: [number, number] } | null> {
  if (!imageUrl || imageScale === null) return null;
  try {
    const image = await readImage(imageUrl);
    // The image sits with its bottom-left corner at 0,0 and is imageScale canvas units per pixel.
    return {
      base64: image.base64,
      modelScale: imageScale * metersPerUnit,
      center: [(image.width * imageScale * metersPerUnit) / 2, (image.height * imageScale * metersPerUnit) / 2],
    };
  } catch (err) {
    console.error('Failed to read the plan image:', err);
    return null;
  }
}

interface CadProjectInput {
  name: string;
  plan: CadPlan;
  imageUrl: string | null;
  imageScale: number | null;
  previewBase64: string;
  settings: { tolerance?: number; maintenance?: number; units?: UnitSystem } | undefined;
  folderId?: number;
  /** The drawing the project is created from. */
  sourceDrawingId?: number;
}

/**
 * The create-project request (multipart) for a polygon plan: the model built from it around a fixed
 * origin (the plan image's centre, or the drawing's), one unlit layer per polygon named like its
 * model node, and the plan itself, so the geometry can be rebuilt in the same place later.
 */
export async function buildCadProjectForm(input: CadProjectInput): Promise<FormData> {
  const { plan } = input;
  const image = await planImage(input.imageUrl, input.imageScale, plan.metersPerUnit);
  const origin = computeOrigin(plan.polygons, plan.metersPerUnit, image?.center ?? null);
  const { model, meshNames } = await buildPlanModel(plan, origin);
  const drawing: DrawingDocument = toDrawingDocument(plan, {
    imageScale: image ? input.imageScale : null,
    origin,
    meshNames: Object.fromEntries(plan.polygons.map((p, index) => [p.id, meshNames[index]])),
  });
  const props: FileMetadataRequest[] = [{ name: CAD_MODEL_FILE_NAME, fileType: FileType.OBJECT_3D }];
  const request: NewProjectRequest = {
    name: input.name,
    description: '',
    tolerance: input.settings?.tolerance ?? 0,
    maintenance: input.settings?.maintenance ?? 0,
    units: input.settings?.units ?? UnitSystem.METRIC,
    latitude: 0,
    longitude: 0,
    previewImageBase64: input.previewBase64,
    referenceImageBase64: image?.base64,
    modelScale: image?.modelScale,
    props,
    lights: [],
    meshes: polygonsToMeshes(plan.polygons, meshNames),
    meshGroups: [],
    folderId: input.folderId,
    drawing,
    ...(input.sourceDrawingId ? { sourceDrawingId: input.sourceDrawingId } : {}),
  };
  const form = new FormData();
  form.append('project', new Blob([JSON.stringify(request)], { type: 'application/json' }));
  form.append('models', model);
  return form;
}
